/**
 * search-lowest-variant.js  (CTS — Cheapest Tshirts / instacustomsplus)
 *
 * Corrects each search result link to point at the TRUE lowest-priced variant,
 * across the WHOLE style family (a style can be split into several separate
 * Shopify products, e.g. one product per size).
 *
 * Why: search-ajax.liquid can only see one product's own variants (capped at 250
 * by Shopify) and has no visibility into sibling/sub-products, so for a split
 * style the "as low as" price and the link's ?variant= can point at the wrong
 * product entirely — not just the wrong variant on the same product, like on CC.
 *
 * Family resolution: metafields are inconsistent across the catalog (only one
 * direction is reliably readable per product), so instead of trusting a single
 * card's own subProductHandles/subProductOfHandle, every result card on the page
 * contributes its own handle + whatever it can read from either field to a
 * union-find graph. Cards that land in the same graph cluster are the same style
 * family, regardless of which one direction happened to be readable on which card.
 * A card with neither field readable just forms its own single-member family
 * (behaves like the CC script: paginates its own variants only).
 *
 * DUPLICATE RESULT CARDS: search-ajax.liquid hides sub-products with
 * {% unless item.metafields.comstack.subProductOfHandle %}, but that metafield
 * has been observed as unreadable via the Storefront API on the sub-handle
 * products (even though it has a value in Admin) — so that Liquid `unless`
 * currently hides nothing and every family member can appear as its own result
 * card. Rather than depend on Liquid/that one field, this script builds a
 * union-find graph per search from whichever metafield direction IS readable on
 * each card, clusters same-family cards together, keeps one (first in DOM
 * order), and removes the rest before applying the link/price fix to the keeper.
 *
 * On any error this silently leaves the Liquid-rendered link as-is.
 *
 * Requires window.CCSearchCfg = { token, domain } (set globally in theme.liquid).
 */
(function () {
  var CFG = window.CCSearchCfg || {};
  var TOKEN = CFG.token || '';
  var DOMAIN = CFG.domain || '';
  if (!TOKEN || !DOMAIN) return; // token not exposed; leave links untouched

  var API = 'https://' + DOMAIN + '/api/2024-01/graphql.json';

  var processed = new WeakSet();
  var cacheByProductId = {}; // productId -> {handle, id, price} (winning variant)

  // Blur the "as low as" price the instant a card appears, so the visitor never
  // sees the (possibly wrong) Liquid-rendered price before we correct it — only
  // the corrected value ever becomes sharp. No CSS/Liquid file changes needed.
  (function injectBlurStyle() {
    if (document.getElementById('js-search-lowest-style')) return;
    var style = document.createElement('style');
    style.id = 'js-search-lowest-style';
    style.textContent = '.js-price-pending{filter:blur(4px);opacity:.55;' +
      'transition:filter .15s ease,opacity .15s ease;}';
    document.head.appendChild(style);
  })();

  var blurred = new WeakSet();
  function blurNewCards() {
    var links = document.querySelectorAll('.js-search-lowest[data-product-id]');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (blurred.has(link)) continue;
      blurred.add(link);
      var priceEl = link.querySelector('.search-bar__item-price');
      if (priceEl) priceEl.classList.add('js-price-pending');
    }
  }
  function revealPrice(link) {
    var priceEl = link.querySelector('.search-bar__item-price');
    if (priceEl) priceEl.classList.remove('js-price-pending');
  }

  function gql(query, variables) {
    return fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function (r) { return r.json(); });
  }

  // comstack.subProductHandles / subProductOfHandle have been observed as a raw
  // comma-separated string (not a proper Shopify "list" metafield JSON array), so
  // strip any [ ] " that might show up if the field type ever changes and split.
  function parseHandleList(raw) {
    if (!raw) return [];
    // Real Shopify handles are always lowercase, but the comstack metafield values
    // have been observed in mixed case (e.g. "...-3898-XS" vs the real "...-3898-xs").
    // product(handle:) is an exact match, so an uncorrected case mismatch silently
    // returns null for every sibling — lowercase everything to avoid that.
    return raw
      .replace(/[\[\]"]/g, '')
      .split(',')
      .map(function (s) { return s.trim().toLowerCase(); })
      .filter(Boolean);
  }

  function uniq(arr) {
    var seen = {};
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      if (!seen[arr[i]]) { seen[arr[i]] = true; out.push(arr[i]); }
    }
    return out;
  }

  // Step 1: look up this product's own grouping metafields + handle.
  function getSelfInfo(productId) {
    var gid = 'gid://shopify/Product/' + productId;
    var q = 'query($id:ID!){product(id:$id){handle ' +
      'subHandles:metafield(namespace:"comstack",key:"subProductHandles"){value} ' +
      'subOf:metafield(namespace:"comstack",key:"subProductOfHandle"){value}}}';
    return gql(q, { id: gid }).then(function (d) {
      var p = d && d.data && d.data.product;
      if (!p || !p.handle) return null;
      return {
        handle: p.handle,
        subHandles: p.subHandles ? p.subHandles.value : null,
        subOf: p.subOf ? p.subOf.value : null
      };
    });
  }

  // This product's own contribution to its family: its own handle, plus anything
  // it points to via subProductHandles (forward) or subProductOfHandle (reverse).
  // Feeds the union-find graph below, so grouping works even when only one
  // direction of the metafield is populated/Storefront-accessible for a card.
  function ownFamilyHandles(self) {
    if (!self) return [];
    var list = [self.handle].concat(parseHandleList(self.subHandles));
    if (self.subOf) list = list.concat(parseHandleList(self.subOf));
    return uniq(list);
  }

  // ---- tiny union-find, used to cluster search-result cards that belong to the
  // same style family across a single fixAll() pass. ----
  var ufParent = {};
  function ufFind(h) {
    var root = h;
    while (ufParent[root] && ufParent[root] !== root) root = ufParent[root];
    while (ufParent[h] && ufParent[h] !== root) { var next = ufParent[h]; ufParent[h] = root; h = next; }
    return root;
  }
  function ufEnsure(h) { if (!ufParent[h]) ufParent[h] = h; }
  function ufUnion(a, b) {
    ufEnsure(a); ufEnsure(b);
    var ra = ufFind(a), rb = ufFind(b);
    if (ra !== rb) ufParent[ra] = rb;
  }

  // Fetch id/handle/variants for a batch of handles in one request, using one
  // aliased `product(handle:$hN)` field per handle (exact match) rather than a
  // `products(query:...)` search — see note above on why "handle:" search is unsafe.
  function fetchFamilyVariants(handles) {
    var varDefs = [];
    var varValues = {};
    var fields = [];
    handles.forEach(function (h, i) {
      var varName = 'h' + i;
      varDefs.push('$' + varName + ':String!');
      varValues[varName] = h;
      fields.push('p' + i + ':product(handle:$' + varName + '){id handle ' +
        'variants(first:250){pageInfo{hasNextPage endCursor}' +
        'edges{node{id price{amount} availableForSale}}}}');
    });
    var q = 'query(' + varDefs.join(',') + '){' + fields.join(' ') + '}';
    return gql(q, varValues).then(function (d) {
      var data = (d && d.data) || {};
      var out = [];
      handles.forEach(function (h, i) {
        var node = data['p' + i];
        if (node) out.push(node);
      });
      return out;
    });
  }

  // Paginate a single product's variants past the first 250 (rare for CTS's
  // ~20-variant sub-products, but the family "head" could have more).
  function fetchRemainingVariants(handle, cursor, acc) {
    var q = 'query($h:String!,$cursor:String){product(handle:$h){' +
      'variants(first:250,after:$cursor){pageInfo{hasNextPage endCursor}' +
      'edges{node{id price{amount} availableForSale}}}}}';
    return gql(q, { h: handle, cursor: cursor }).then(function (d) {
      var p = d && d.data && d.data.product;
      if (!p) return acc;
      var v = p.variants;
      acc = acc.concat(v.edges);
      if (v.pageInfo.hasNextPage) return fetchRemainingVariants(handle, v.pageInfo.endCursor, acc);
      return acc;
    });
  }

  // Find the lowest-priced AVAILABLE variant across every product in the family.
  // Falls back to the lowest-priced variant overall if nothing is in stock, so
  // the link is never broken.
  function lowestAcrossFamily(handles) {
    return fetchFamilyVariants(handles).then(function (nodes) {
      var pending = nodes.map(function (node) {
        if (node.variants.pageInfo.hasNextPage) {
          return fetchRemainingVariants(node.handle, node.variants.pageInfo.endCursor, node.variants.edges.slice())
            .then(function (allEdges) { return { handle: node.handle, edges: allEdges }; });
        }
        return Promise.resolve({ handle: node.handle, edges: node.variants.edges });
      });

      return Promise.all(pending).then(function (results) {
        var lowAvail = { handle: null, id: null, price: Infinity };
        var lowAny = { handle: null, id: null, price: Infinity };

        results.forEach(function (r) {
          r.edges.forEach(function (e) {
            var pr = parseFloat(e.node.price.amount);
            if (isNaN(pr)) return;
            var vid = e.node.id.replace('gid://shopify/ProductVariant/', '');
            if (pr < lowAny.price) { lowAny = { handle: r.handle, id: vid, price: pr }; }
            if (e.node.availableForSale && pr < lowAvail.price) {
              lowAvail = { handle: r.handle, id: vid, price: pr };
            }
          });
        });

        return lowAvail.id ? lowAvail : lowAny;
      });
    });
  }

  // Minimal Shopify money formatter (mirrors the standard money_format.js snippet),
  // reusing window.theme.moneyFormat (already exposed in theme.liquid) so the
  // rewritten price text matches the shop's configured format/currency symbol.
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(cents) || cents == null) return '0';
    var value = (cents / 100).toFixed(precision);
    var parts = value.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var frac = parts[1] ? decimal + parts[1] : '';
    return dollars + frac;
  }

  function formatMoney(amountDollars) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var cents = Math.round(amountDollars * 100);
    var m = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var kind = m ? m[1] : 'amount';
    var value;
    switch (kind) {
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return m ? format.replace(m[0], value) : format;
  }

  function buildHref(currentHref, targetHandle, variantId) {
    // The winning variant may live on a DIFFERENT product than the one Liquid
    // linked to (that's the whole point for split styles) — so rebuild the path
    // from the target handle rather than just patching the query string.
    var path = '/products/' + targetHandle;

    var params = new URLSearchParams();
    var qIndex = currentHref.indexOf('?');
    if (qIndex !== -1) {
      // Preserve tracking params (_pos/_sid/_ss); normalize any stray extra "?" to "&".
      var existing = currentHref.substring(qIndex + 1).replace(/\?/g, '&');
      params = new URLSearchParams(existing);
    }
    params.set('variant', variantId);

    return path + '?' + params.toString();
  }

  function applyFix(link, best) {
    if (!best || !best.id || !best.handle) return;
    var href = link.getAttribute('href');
    var newHref = buildHref(href, best.handle, best.id);
    if (href !== newHref) {
      link.setAttribute('href', newHref);
    }
    repairDoubleQuestionOn(link);

    // Also correct the displayed "as low as" price — Liquid only ever shows this
    // product's own price, not the true family-wide lowest.
    var priceEl = link.querySelector('.search-bar__item-price');
    if (priceEl && typeof best.price === 'number' && !isNaN(best.price)) {
      priceEl.textContent = formatMoney(best.price);
    }
  }

  function repairDoubleQuestionOn(link) {
    var h = link.getAttribute('href');
    var first = h.indexOf('?');
    if (first !== -1 && h.indexOf('?', first + 1) !== -1) {
      link.setAttribute('href', h.substring(0, first + 1) + h.substring(first + 1).replace(/\?/g, '&'));
    }
  }

  // The theme may re-append tracking params (_pos/_sid/_ss) to result links after
  // we set them, sometimes producing a stray second "?". Repair any such links.
  function repairDoubleQuestion() {
    var links = document.querySelectorAll('.js-search-lowest[href]');
    for (var i = 0; i < links.length; i++) repairDoubleQuestionOn(links[i]);
  }

  // For every NEW result card: figure out which style family it belongs to
  // (via the union-find graph), keep only one card per family (first in DOM
  // order, so the visible layout doesn't jump), remove the rest as duplicates,
  // then correct the keeper's link + price using the combined family variants.
  function fixAll() {
    var links = Array.prototype.slice.call(document.querySelectorAll('.js-search-lowest[data-product-id]'));
    var toProcess = links.filter(function (l) { return !processed.has(l); });
    toProcess.forEach(function (l) { processed.add(l); });

    if (!toProcess.length) {
      repairDoubleQuestion();
      return;
    }

    Promise.all(toProcess.map(function (link) {
      var pid = link.getAttribute('data-product-id');
      return getSelfInfo(pid)
        .then(function (self) { return { link: link, pid: pid, self: self }; })
        .catch(function () { return { link: link, pid: pid, self: null }; });
    })).then(function (entries) {
      // Build the union-find graph from every card's own forward/reverse metafields.
      entries.forEach(function (e) {
        if (!e.self) return;
        var own = ownFamilyHandles(e.self);
        if (!own.length) return;
        ufEnsure(own[0]);
        for (var i = 1; i < own.length; i++) ufUnion(own[0], own[i]);
      });

      // Cluster the DOM cards by their union-find root (cards with no usable
      // metafields become their own standalone group, keyed by product id).
      var groups = {};
      entries.forEach(function (e) {
        var key = e.self ? ufFind(e.self.handle) : ('__standalone__' + e.pid);
        (groups[key] = groups[key] || []).push(e);
      });

      Object.keys(groups).forEach(function (key) {
        var group = groups[key];
        var keeper = group[0]; // first in DOM order

        // Remove duplicate sub-product cards — search-ajax.liquid's own
        // `unless subProductOfHandle` hide check is unreliable (see notes above),
        // so we do the dedupe here instead, using whichever metafield direction
        // was actually readable.
        for (var i = 1; i < group.length; i++) group[i].link.remove();

        // Combined family = union of every group member's own list, so we don't
        // miss a sibling even if the keeper's own metafields are sparse.
        var familyHandles = [];
        group.forEach(function (e) { familyHandles = familyHandles.concat(ownFamilyHandles(e.self)); });
        familyHandles = uniq(familyHandles);
        if (!familyHandles.length) { revealPrice(keeper.link); return; } // metafields unreadable; leave Liquid link/price as-is

        if (cacheByProductId[keeper.pid]) {
          applyFix(keeper.link, cacheByProductId[keeper.pid]);
          revealPrice(keeper.link);
          return;
        }

        lowestAcrossFamily(familyHandles).then(function (best) {
          if (best && best.id && best.handle) cacheByProductId[keeper.pid] = best;
          applyFix(keeper.link, best);
          revealPrice(keeper.link);
        }).catch(function () { revealPrice(keeper.link); /* leave Liquid link/price as-is */ });
      });

      repairDoubleQuestion();
    }).catch(function () { repairDoubleQuestion(); });
  }

  // Search results are injected via AJAX; watch for them and fix on appearance.
  if (window.MutationObserver) {
    var t = null;
    var mo = new MutationObserver(function () {
      repairDoubleQuestion(); // cheap, run immediately every mutation
      blurNewCards(); // also immediate — must happen before the wrong price ever paints
      if (t) return;
      t = setTimeout(function () { t = null; fixAll(); }, 200);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { blurNewCards(); fixAll(); });
  } else {
    blurNewCards();
    fixAll();
  }

  // Final safety net: repair a malformed href at the moment of click, so even if
  // the theme re-appends params after our fix, the navigated URL is valid.
  document.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('.js-search-lowest[href]') : null;
    if (!a) return;
    repairDoubleQuestionOn(a);
  }, true);
})();